import { useMutation } from '@tanstack/react-query';
import * as Icons from 'lucide-react';
import { Puzzle, type LucideIcon } from 'lucide-react';
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { Button, Modal, toast, type DropdownItem } from '@cyphex/ui';
import type { MessageId, Uuid } from '@cyphex/types/common';
import type { MessageAction } from '@cyphex/types/platform';
import { platformApi } from '../api/platformApi';
import { useMessageActions } from './useCommandRegistry';

export interface AppMessageActions {
  /** Append these to a message's "…" menu. Empty when no app contributes one. */
  items: DropdownItem[];
  /** Render alongside the menu — the confirmation dialog lives here. */
  dialog: ReactNode;
}

/**
 * App contributions to a message's "…" menu.
 *
 * A `.tsx` hook because it owns a dialog. Splitting the confirmation into a
 * separate component that the bar also has to mount would mean every consumer
 * wires up two things and one of them eventually gets forgotten — and the one
 * that gets forgotten is the confirmation.
 *
 * CONFIRMATION IS THE APP'S CHOICE, not ours. An action that files a ticket in
 * someone's production tracker sets `confirmText`; a translate action does not.
 * We never invent a prompt for an action that did not ask for one, because a
 * dialog on a harmless action trains people to dismiss dialogs.
 */
export function useAppMessageActions(messageId: MessageId): AppMessageActions {
  const { data: actions } = useMessageActions();
  const [pending, setPending] = useState<MessageAction | null>(null);

  const invoke = useMutation({
    mutationFn: (actionId: Uuid) => platformApi.invokeMessageAction(actionId, messageId),
    onSuccess: (result) => {
      setPending(null);
      if (result.text !== null) {
        if (result.ok) toast.success(result.text);
        else toast.error(result.text);
      }
    },
    onError: () => {
      setPending(null);
      toast.error('That action could not be run.');
    },
  });

  const run = useCallback(
    (action: MessageAction) => {
      if (action.confirmText !== null) {
        setPending(action);
        return;
      }
      invoke.mutate(action.id);
    },
    [invoke],
  );

  const items = useMemo<DropdownItem[]>(() => {
    const available = actions ?? [];
    return available.map((action, index) => ({
      id: `app-action-${action.id}`,
      label: action.name,
      icon: iconFor(action.icon),
      // A hairline above the FIRST app entry only, so third-party additions read
      // as a distinct group rather than as more of our own menu.
      separatorBefore: index === 0,
      onSelect: () => run(action),
    }));
  }, [actions, run]);

  const dialog =
    pending === null ? null : (
      <Modal
        open
        onOpenChange={(open) => {
          if (!open) setPending(null);
        }}
        title={pending.name}
        description={pending.appName ?? 'An installed app'}
        footer={
          <>
            <Button variant="outline-neutral" onClick={() => setPending(null)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={invoke.isPending}
              onClick={() => invoke.mutate(pending.id)}
            >
              Continue
            </Button>
          </>
        }
      >
        <p className="text-md text-foreground">{pending.confirmText}</p>
        <p className="mt-2 text-sm text-muted-foreground">
          This message&rsquo;s text and the ids around it are sent to the app. No token is granted
          and nothing else is shared.
        </p>
      </Modal>
    );

  return { items, dialog };
}

/** Tenant-supplied icon name → lucide component, falling back to a puzzle piece. */
function iconFor(name: string | null): LucideIcon {
  if (name === null) return Puzzle;
  const candidate = (Icons as unknown as Record<string, unknown>)[name];
  return typeof candidate === 'function' || typeof candidate === 'object'
    ? (candidate as LucideIcon)
    : Puzzle;
}
